import {
  useMergedShopFormulas,
  useShopifyMultipass,
  FormulaProduct,
} from '@viome/viome-base/src/usecase/Shop/ShopQuery';
import { useFindPlanBySkuSet } from '@viome/viome-base/src/usecase/Shop/ShopUtil';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import {
  CustomizeModeView,
  AllSubscribedView,
  AnySubscribedView,
  NotSubscribedView,
} from './components/ShopPageViews';
import { useState, useEffect, useMemo } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { ShopFormulaSkeleton } from '@screens/ShopPage/components/ShopFormulaSkeleton';
import EventTracker from '@utils/EventTracker';
import { analyticsEvent } from '@utils/analyticsEvent';

export interface ShopFormulasPageProps {
  onCustomizeModeChange?: (isCustomizing: boolean) => void;
}

export const ShopFormulasPage = ({
  onCustomizeModeChange,
}: ShopFormulasPageProps) => {
  const navigate = useNavigate();
  const {
    data: apiData,
    isLoading: isLoadingFormulas,
    isError: isErrorFormulas,
  } = useMergedShopFormulas();

  const products: FormulaProduct[] | undefined = apiData?.products;
  const promo = apiData?.promo;
  const offerDuration = apiData?.offerDuration;

  const [isCustomizeMode, setIsCustomizeMode] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(
    new Set(),
  );

  const { mutate: generateMultipassUrl, isPending: isMultipassLoading } =
    useShopifyMultipass();
  const { toast } = useToast();

  useEffect(() => {
    if (onCustomizeModeChange) {
      onCustomizeModeChange(isCustomizeMode);
    }
  }, [isCustomizeMode, onCustomizeModeChange]);

  const { isAnySubscribed, isAllSubscribed, nonSubscribedProducts } =
    useMemo(() => {
      if (!products) {
        return {
          isAnySubscribed: false,
          isAllSubscribed: false,
          nonSubscribedProducts: [],
          subscribedProducts: [],
        };
      }
      const anySubscribed = products.some((p) => p.is_subscribed);
      const allSubscribed = products.every((p) => p.is_subscribed);
      return {
        isAnySubscribed: anySubscribed,
        isAllSubscribed: allSubscribed,
        nonSubscribedProducts: products.filter((p) => !p.is_subscribed),
        subscribedProducts: products.filter((p) => p.is_subscribed),
      };
    }, [products]);

  // Default pricing object when no price can be determined or
  // when explicitly showing "Select items".
  const unavailablePricing = useMemo(
    () => ({
      displayMonthlyPrice: 'N/A',
      greenDaily: 'N/A',
      strikethroughDaily: null,
      isPriceAvailable: false,
      hasDiscount: false,
      monthlyPriceWithOutDiscount: 'N/A',
      offerDuration: 'N/A',
    }),
    [],
  );

  const currentSelectedSkusArray = useMemo(() => {
    if (!products) return [];
    const skus = new Set<string>();
    products.forEach((p) => {
      if (selectedProductIds.has(String(p.product_id))) {
        skus.add(p.sku);
      }
    });
    return Array.from(skus);
  }, [products, selectedProductIds]);

  const { plan: matchedPlanForSelected } = useFindPlanBySkuSet(
    currentSelectedSkusArray,
  );

  const allNonSubscribedSkusArray = useMemo(() => {
    return nonSubscribedProducts.map((p) => p.sku);
  }, [nonSubscribedProducts]);

  const { plan: planForNonSubscribed } = useFindPlanBySkuSet(
    allNonSubscribedSkusArray,
  );

  const dynamicPricing = useMemo(() => {
    if (isAnySubscribed) {
      return unavailablePricing;
    }

    const discountPercentageFromApi = apiData?.discountPercentage;
    const actualDiscount =
      typeof discountPercentageFromApi === 'number' &&
      discountPercentageFromApi > 0
        ? discountPercentageFromApi
        : 0;
    const hasActualDiscount = actualDiscount > 0;

    let planForPricing;

    if (currentSelectedSkusArray.length > 0) {
      planForPricing = matchedPlanForSelected;
    } else {
      if (isCustomizeMode) {
        return unavailablePricing;
      }
      if (nonSubscribedProducts.length > 0) {
        planForPricing = planForNonSubscribed;
      }
    }

    if (planForPricing && planForPricing.plan_product) {
      const planPrice = parseFloat(planForPricing.plan_product.price);

      if (hasActualDiscount) {
        const discountedMonthlyPrice = planPrice * (1 - actualDiscount / 100);
        return {
          displayMonthlyPrice: discountedMonthlyPrice.toFixed(2),
          greenDaily: (discountedMonthlyPrice / 30).toFixed(2),
          strikethroughDaily: (planPrice / 30).toFixed(2),
          isPriceAvailable: true,
          hasDiscount: true,
          monthlyPriceWithOutDiscount: planPrice.toFixed(2),
          offerDuration: offerDuration,
        };
      } else {
        return {
          displayMonthlyPrice: planPrice.toFixed(2),
          greenDaily: (planPrice / 30).toFixed(2),
          strikethroughDaily: null,
          isPriceAvailable: true,
          hasDiscount: false,
          monthlyPriceWithOutDiscount: planPrice.toFixed(2),
          offerDuration: offerDuration,
        };
      }
    }
    return unavailablePricing;
  }, [
    apiData,
    isAnySubscribed,
    currentSelectedSkusArray,
    matchedPlanForSelected,
    planForNonSubscribed,
    isCustomizeMode,
    nonSubscribedProducts,
    unavailablePricing,
  ]);

  const pricingForUpgrade = useMemo(() => {
    if (
      isAnySubscribed &&
      nonSubscribedProducts.length > 0 &&
      planForNonSubscribed &&
      planForNonSubscribed.plan_product
    ) {
      const planPrice = parseFloat(planForNonSubscribed.plan_product.price);
      const discountPercentageFromApi = apiData?.discountPercentage;
      const actualDiscount =
        typeof discountPercentageFromApi === 'number' &&
        discountPercentageFromApi > 0
          ? discountPercentageFromApi
          : 0;
      const hasActualDiscount = actualDiscount > 0;

      if (hasActualDiscount) {
        const discountedMonthlyPrice = planPrice * (1 - actualDiscount / 100);
        return {
          displayMonthlyPrice: discountedMonthlyPrice.toFixed(2),
          greenDaily: (discountedMonthlyPrice / 30).toFixed(2),
          strikethroughDaily: (planPrice / 30).toFixed(2),
          isPriceAvailable: true,
          hasDiscount: true,
          monthlyPriceWithOutDiscount: planPrice.toFixed(2),
          offerDuration: offerDuration,
        };
      } else {
        return {
          displayMonthlyPrice: planPrice.toFixed(2),
          greenDaily: (planPrice / 30).toFixed(2),
          strikethroughDaily: null,
          isPriceAvailable: true,
          hasDiscount: false,
          monthlyPriceWithOutDiscount: planPrice.toFixed(2),
          offerDuration: offerDuration,
        };
      }
    }
    return unavailablePricing;
  }, [
    apiData,
    isAnySubscribed,
    nonSubscribedProducts,
    planForNonSubscribed,
    unavailablePricing,
  ]);

  const handleCtaClick = () => {
    if (isAnySubscribed) {
      navigate('/smp');
      return;
    }

    let planToUse;

    if (isCustomizeMode) {
      if (currentSelectedSkusArray.length > 0) {
        planToUse = matchedPlanForSelected;
      }
      EventTracker.trackEvent('shop', analyticsEvent.SHOP_MYFORMULAS_CUSTOMIZE_CONTINUETOORDERCTA);
    } else {
      if (currentSelectedSkusArray.length > 0) {
        planToUse = matchedPlanForSelected;
      } else {
        planToUse = planForNonSubscribed;
      }
      EventTracker.trackEvent('shop', analyticsEvent.SHOP_MYFORMULAS_CONTINUETOORDERCTA);
    }

    const shopifyCartUrlFromPlan = planToUse?.plan_product?.shopify_cart_url;

    if (!shopifyCartUrlFromPlan) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Shopify cart URL not found for the current plan.',
      });
      console.error('Shopify cart URL not found for the current plan.');
      return;
    }

    let finalShopifyCartUrl = shopifyCartUrlFromPlan;
    const couponCodeValue = apiData?.couponCode;

    if (couponCodeValue) {
      const separator = finalShopifyCartUrl.includes('?') ? '&' : '?';
      finalShopifyCartUrl += `${separator}discount=${couponCodeValue}`;
    }

    generateMultipassUrl(finalShopifyCartUrl, {
      onSuccess: (multipassData) => {
        if (multipassData) {
          window.open(multipassData, '_blank');
        } else {
          toast({
            variant: 'destructive',
            title: 'Error',
            description: 'Could not retrieve checkout URL. Please try again.',
          });
          console.error(
            'Multipass URL generation succeeded but no URL was returned or data is invalid.',
            multipassData,
          );
        }
      },
      onError: (error: Error) => {
        toast({
          variant: 'destructive',
          title: 'Error',
          description:
            error.message || 'Could not proceed to checkout. Please try again.',
        });
        console.error('Error during Shopify multipass process:', error);
      },
    });
  };

  if (isLoadingFormulas) {
    return (
      <ShopFormulaSkeleton />
    );
  }

  if (isErrorFormulas || !products) {
    return <ErrorLoadingPage errorType="error" hideNavBar />;
  }

  const handleCustomizeClick = () => {
    if (nonSubscribedProducts && nonSubscribedProducts.length > 0) {
      const currentNonSubscribedSelections = new Set<string>();
      if (selectedProductIds.size > 0) {
        nonSubscribedProducts.forEach((nsp) => {
          if (selectedProductIds.has(String(nsp.product_id))) {
            currentNonSubscribedSelections.add(String(nsp.product_id));
          }
        });
      }

      if (currentNonSubscribedSelections.size === 0) {
        setSelectedProductIds(
          new Set(nonSubscribedProducts.map((p) => String(p.product_id))),
        );
      } else {
        setSelectedProductIds(currentNonSubscribedSelections);
      }
    }
    setIsCustomizeMode(true);
    EventTracker.trackEvent('shop', analyticsEvent.SHOP_MYFORMULAS_CUSTOMIZE_BUTTON);
  };

  const handleBackClick = () => {
    setIsCustomizeMode(false);
  };

  const handleToggleProductSelected = (productId: string) => {
    setSelectedProductIds((prevSelectedIds) => {
      const newSelectedIds = new Set(prevSelectedIds);
      if (newSelectedIds.has(productId)) {
        newSelectedIds.delete(productId);
      } else {
        newSelectedIds.add(productId);
      }
      return newSelectedIds;
    });
  };

  const handleSelectAll = () => {
    if (nonSubscribedProducts && nonSubscribedProducts.length > 0) {
      setSelectedProductIds(
        new Set(nonSubscribedProducts.map((p) => String(p.product_id))),
      );
    }
    EventTracker.trackEvent('shop', analyticsEvent.SHOP_MYFORMULAS_CUSTOMIZE_SELECTALL);
  };

  const ctaButtonLabel = isAnySubscribed
    ? 'Change My Plan'
    : 'Continue to Order';

  if (isCustomizeMode) {
    return (
      <CustomizeModeView
        handleBackClick={handleBackClick}
        nonSubscribedProducts={nonSubscribedProducts}
        selectedProductIds={selectedProductIds}
        handleToggleProductSelected={handleToggleProductSelected}
        handleSelectAll={handleSelectAll}
        dynamicPricing={dynamicPricing}
        ctaButtonLabel={ctaButtonLabel}
        onCtaClick={handleCtaClick}
        isCtaLoading={isMultipassLoading}
      />
    );
  }

  if (isAllSubscribed) {
    return <AllSubscribedView products={products} />;
  } else if (isAnySubscribed) {
    return (
      <AnySubscribedView
        pricingForUpgrade={pricingForUpgrade}
        ctaButtonLabel={ctaButtonLabel}
        nonSubscribedProducts={nonSubscribedProducts}
        products={products}
        onCtaClick={handleCtaClick}
        isCtaLoading={isMultipassLoading}
      />
    );
  } else {
    return (
      <NotSubscribedView
        promotionData={promo}
        dynamicPricing={dynamicPricing}
        ctaButtonLabel={ctaButtonLabel}
        selectedProductIds={selectedProductIds}
        nonSubscribedProducts={nonSubscribedProducts}
        products={products}
        handleCustomizeClick={handleCustomizeClick}
        isAnySubscribed={isAnySubscribed}
        onCtaClick={handleCtaClick}
        isCtaLoading={isMultipassLoading}
      />
    );
  }
};
